import type { ExportAnswerFill, FeedbackAnswerQuestionFill, GapQuestionFill, QuestionFillData } from '@repo/types/questions/QuestionFill';

import { QuestionFill } from '@repo/lib/quiz/questions/class/QuestionFill';
import { fillGapRegex } from '@repo/schemas/utils/fillGap';
import { useMemo } from 'react';

import type { BadgeProps } from '@/components/ui/badge';

export interface ProcessedFillData {
  content: string;
  gap: GapQuestionFill | undefined;
}

/**
 * Extracts gap content from a slice by removing brackets
 */
export function getGapContent(slice: string): string {
  return slice.replace(/^\[|\]$/g, '').trim();
}

/**
 * Processes fill question gapsContent into structured data
 */
export function processFillQuestionData(question: QuestionFillData): ProcessedFillData[] {
  if (!question.gapsContent) {
    return [];
  }

  const gapsContentPartials: string[] = question.gapsContent.split(fillGapRegex).filter(Boolean);

  return gapsContentPartials.map((slice: string) => {
    const gapContent: string = getGapContent(slice);
    const gapData = question.gaps?.find((gap) => gap.id === gapContent) ?? undefined;
    return {
      content: gapContent,
      gap: gapData,
    };
  });
}

/**
 * Gets display content for a gap or regular content
 */
export function getFillContent(content: string, gap: GapQuestionFill | undefined): string {
  if (gap) {
    return gap.options.map((option) => option.content).join('/');
  }
  return content;
}

export function useQuestionMarkedFill(marked: FeedbackAnswerQuestionFill | null, question: QuestionFillData, answer: ExportAnswerFill | undefined) {
  const questionObj = useMemo<QuestionFill>(() => {
    const qClass = new QuestionFill(question, null, marked);
    if (answer) qClass.importAnswer(answer);

    return qClass;
  }, [question, marked, answer]);

  const correctState = questionObj.correctState();

  const data = processFillQuestionData(question);

  const markedAnswers = Object.entries(marked ?? {});
  const gapAnswers = Object.entries(question.gaps);

  const score: number = questionObj.getPoints();
  const totalPoints: number = questionObj.getMaxPoints();
  const correct: boolean | null = markedAnswers.every((answer) => answer[1]?.correct ?? null);

  const isMarked: boolean = questionObj.isMarked();

  // Keep getContent for backward compatibility, but use the shared utility
  const getContent = getFillContent;

  const getAnswerContent = (content: string, gap: GapQuestionFill | undefined): [string | undefined, BadgeProps['variant'], string | null] => {
    const gapOptions: GapQuestionFill['options'] | undefined = gap?.options;
    const gapAnswer = markedAnswers.find((answer) => answer[0] === gap?.id);
    const gapCorrect: boolean | undefined = gapAnswer?.[1]?.correct;
    const explanation: string | null = gapAnswer?.[1] && gapAnswer[1].explanation ? gapAnswer[1].explanation : null;

    const variant: BadgeProps['variant'] = gapCorrect === undefined ? 'warning' : gapCorrect ? 'success' : 'destructive';
    if (gap) {
      const gapContent: string | undefined = gapOptions?.find((option) => option.id === gapAnswer?.[1]?.id)?.content ?? gapOptions?.map((option) => option.content).join('/');
      return [gapContent, variant, explanation];
    }
    return [content, variant, explanation];
  };

  return {
    getContent,
    getAnswerContent,
    markedAnswers,
    gapAnswers,
    score,
    totalPoints,
    correct,
    data,
    isMarked,
    correctState,
    questionObj,
  };
}
